import { designApiKey } from '../../lib/elevenLabsKey'
import type { Episode, PodcastSummary } from './types'

export const LISTENNOTES_BASE =
  import.meta.env.VITE_LISTENNOTES_BASE_URL ??
  'https://listen-api.listennotes.com/api/v2'

const LISTENNOTES_API_KEY = designApiKey(
  (import.meta.env.VITE_LISTENNOTES_API_KEY ?? '').trim(),
)

function authHeaders(): HeadersInit {
  return LISTENNOTES_API_KEY ? { 'X-ListenAPI-Key': LISTENNOTES_API_KEY } : {}
}

const SEARCH_PAGE_SIZE = 10

function stripHtml(text: string): string {
  return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

function clamp(text: string, max: number): string {
  if (text.length <= max) return text
  return text.slice(0, max - 1).trimEnd() + '…'
}

type RawPodcast = {
  id: string
  title_original?: string
  title?: string
  publisher_original?: string
  publisher?: string
  image?: string
  thumbnail?: string
  description_original?: string
  description?: string
}

type RawEpisode = {
  id: string
  title_original?: string
  title?: string
  description_original?: string
  description?: string
  audio?: string
  audio_length_sec?: number
  pub_date_ms?: number
  thumbnail?: string
  image?: string
}

function mapPodcastSummary(raw: RawPodcast, descClampLen = 200): PodcastSummary {
  const description = clamp(
    stripHtml(raw.description_original ?? raw.description ?? ''),
    descClampLen,
  )
  return {
    id: raw.id,
    title: raw.title_original ?? raw.title ?? '',
    publisher: raw.publisher_original ?? raw.publisher ?? '',
    image: raw.image ?? raw.thumbnail ?? '',
    thumbnail: raw.thumbnail ?? raw.image ?? '',
    description,
  }
}

function mapEpisode(
  raw: RawEpisode,
  podcastId: string,
  podcastTitle: string,
): Episode {
  const description = clamp(
    stripHtml(raw.description_original ?? raw.description ?? ''),
    600,
  )
  return {
    id: raw.id,
    title: raw.title_original ?? raw.title ?? '',
    description,
    audio: raw.audio ?? '',
    audioLengthSec: raw.audio_length_sec ?? 0,
    pubDateMs: raw.pub_date_ms ?? 0,
    thumbnail: raw.thumbnail ?? raw.image ?? '',
    image: raw.image ?? raw.thumbnail ?? '',
    podcastId,
    podcastTitle,
  }
}

export async function searchPodcasts(
  q: string,
  offset = 0,
): Promise<{ results: PodcastSummary[]; nextOffset: number | null }> {
  const params = new URLSearchParams({
    q,
    type: 'podcast',
    offset: String(offset),
    page_size: String(SEARCH_PAGE_SIZE),
  })
  const res = await fetch(`${LISTENNOTES_BASE}/search?${params.toString()}`, {
    headers: authHeaders(),
  })
  if (!res.ok) {
    throw new Error(`Podcast search failed (${res.status})`)
  }
  const json = (await res.json()) as {
    results?: RawPodcast[]
    next_offset?: number
    total?: number
    count?: number
  }
  const rawResults = json.results ?? []
  const results = rawResults.map((r) => mapPodcastSummary(r, 200))
  const total = json.total ?? 0
  const nextOffsetRaw = json.next_offset ?? null
  const consumed = offset + rawResults.length
  const hasMore =
    rawResults.length === SEARCH_PAGE_SIZE &&
    nextOffsetRaw !== null &&
    nextOffsetRaw > offset &&
    (total === 0 || consumed < total)
  return {
    results,
    nextOffset: hasMore ? nextOffsetRaw : null,
  }
}

export async function getBestPodcasts(
  genreId: number,
  page = 1,
): Promise<PodcastSummary[]> {
  const params = new URLSearchParams({
    genre_id: String(genreId),
    page: String(page),
  })
  const res = await fetch(
    `${LISTENNOTES_BASE}/best_podcasts?${params.toString()}`,
    { headers: authHeaders() },
  )
  if (!res.ok) {
    throw new Error(`Best podcasts fetch failed (${res.status})`)
  }
  const json = (await res.json()) as { podcasts?: RawPodcast[] }
  const raw = json.podcasts ?? []
  return raw.map((p) => mapPodcastSummary(p, 200))
}

export async function getPodcast(
  id: string,
  nextPubDateMs?: number,
): Promise<{
  podcast: PodcastSummary
  episodes: Episode[]
  nextPubDateMs: number | null
}> {
  const params = new URLSearchParams({ sort: 'recent_first' })
  if (typeof nextPubDateMs === 'number') {
    params.set('next_episode_pub_date', String(nextPubDateMs))
  }
  const res = await fetch(
    `${LISTENNOTES_BASE}/podcasts/${id}?${params.toString()}`,
    { headers: authHeaders() },
  )
  if (!res.ok) {
    throw new Error(`Podcast fetch failed (${res.status})`)
  }
  const json = (await res.json()) as RawPodcast & {
    episodes?: RawEpisode[]
    next_episode_pub_date?: number
    total_episodes?: number
  }
  const podcast = mapPodcastSummary(json, 600)
  const rawEpisodes = json.episodes ?? []
  const episodes = rawEpisodes.map((e) => mapEpisode(e, podcast.id, podcast.title))
  const nextRaw = json.next_episode_pub_date ?? null
  const hasMore =
    rawEpisodes.length > 0 &&
    nextRaw !== null &&
    (typeof nextPubDateMs !== 'number' || nextRaw < nextPubDateMs)
  return {
    podcast,
    episodes,
    nextPubDateMs: hasMore ? nextRaw : null,
  }
}
